import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronRight, CircleCheck } from 'lucide-react'
import type {
  AddDictionaryWordOutcome,
  ClearDictionaryOutcome,
  RemoveDictionaryWordOutcome,
} from '@/api/types/dictionary'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Toggle } from '@/components/ui/toggle'
import { cn } from '@/utils'
import {
  type SpellCheckState,
  type SpellIssue,
  changeAllOccurrences,
  changeOccurrence,
  firstOpenOccurrenceIndex,
  ignoreAllOccurrences,
  ignoreOccurrence,
  openOccurrenceCount,
  totalMisspellings,
  visibleSpellIssues,
} from '@/lib/spell-check/spell-check-state'
import { SpellCheckStatusLine } from './components/spell-check-status-line'
import { EditDictionaryTab } from './edit-dictionary-tab'
import { usePopoverDictionaryAdd } from './hooks/use-popover-dictionary-add'
import { useSpellCanvasClick } from './hooks/use-spell-canvas-click'
import { useSpellWordAnchor } from './hooks/use-spell-word-anchor'
import { SpellFixPopoverContent } from './spell-fix-popover-content'
import type { DictionaryStatus } from './types/dictionary-status'
import type { SpellCheckStatus } from './types/spell-check-status'
import type { SpellFocusWord } from './types/spell-focus-word'

export interface SpellCheckPanelProps {
  state: SpellCheckState
  /** `loading` until the wordlist and dictionary are in; `error` when either fails to load. */
  status: SpellCheckStatus
  /** The school dictionary fetch alone, for the Edit Dictionary tab. */
  dictionaryStatus: DictionaryStatus
  onStateChange: (next: SpellCheckState) => void
  /** Adds a custom word from Edit Dictionary and reports how it ended. */
  onAddWord: (word: string) => Promise<AddDictionaryWordOutcome>
  /** Removes a custom word from Edit Dictionary and reports how it ended. */
  onRemoveWord: (word: string) => Promise<RemoveDictionaryWordOutcome>
  /** Clears every custom word from Edit Dictionary and reports how it ended. */
  onClearAll: () => Promise<ClearDictionaryOutcome>
  /** Suggestions for a word, asked for only when its popover opens. */
  getSuggestions: (word: string) => string[]
  /**
   * Called with the misspelled word the open popover is on, or `null` when it is closed. The canvas
   * marks the word so the popover can open under it, and, when the word is in hidden (overflow)
   * text, selects its frame and shows that text meanwhile.
   */
  onFocusWord: (word: SpellFocusWord | null) => void
  /** Try loading the wordlist and dictionary again, from the status line's Retry button. */
  onRetry: () => void
}

interface ActiveIssue {
  id: string
  occurrenceIndex: number
  /**
   * Set when Case Sensitive flips with the popover open. Words regroup under new ids, so the
   * popover finds its instance again by where it sits in the text (ED-5).
   */
  anchor?: { shapeId: string; start: number }
}

type PanelTab = 'check' | 'dictionary'

/** Outline count/"Overflow" chip used inside issue rows (Figma Badge / Outline). */
const ROW_BADGE_CLASS = 'h-5 bg-background text-foreground'

function SwitchRow({
  label,
  checked,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
      <span className="pt-[3px] text-sm leading-none font-medium text-foreground">{label}</span>
    </label>
  )
}

/**
 * Spread-level Check Spelling panel (Figma "Check Spelling Panel": Default / Clean / Dictionary /
 * Dictionary Empty). Fully controlled — every edit goes through `onStateChange`.
 */
export function SpellCheckPanel({
  state,
  status,
  dictionaryStatus,
  onStateChange,
  onAddWord,
  onRemoveWord,
  onClearAll,
  getSuggestions,
  onFocusWord,
  onRetry,
}: SpellCheckPanelProps) {
  const [activeIssue, setActiveIssue] = useState<ActiveIssue | null>(null)
  const [tab, setTab] = useState<PanelTab>('check')
  const caseSensitiveRowRef = useRef<HTMLDivElement>(null)
  const tabsListRef = useRef<HTMLDivElement>(null)
  const issues = visibleSpellIssues(state)
  const misspellingCount = totalMisspellings(state)

  // After Case Sensitive flips, the open popover moves to the issue that now holds its instance,
  // with that issue's count, or closes when the word is no longer flagged (ED-5).
  const anchor = activeIssue?.anchor

  if (anchor) {
    const isAnchor = (o: SpellIssue['occurrences'][number]) =>
      o.status === 'open' && o.shapeId === anchor.shapeId && o.start === anchor.start
    const issue = issues.find((i) => i.occurrences.some(isAnchor))

    setActiveIssue(
      issue ? { id: issue.id, occurrenceIndex: issue.occurrences.findIndex(isAnchor) } : null
    )
  }

  // The popover is on one instance of one word. When that instance is in hidden text, its frame is
  // the one to peek into (WMPCM-1212, SC-5); a visible instance, or no popover, peeks into nothing.
  // On Edit Dictionary the popover is hidden but kept, so it is open again on the way back.
  const shownIssue = tab === 'check' ? activeIssue : null
  const activeOccurrence = shownIssue
    ? issues.find((issue) => issue.id === shownIssue.id)?.occurrences[shownIssue.occurrenceIndex]
    : undefined

  // Where that instance sits on the canvas. A word on a path has no frame to open under.
  const wordShapeId = activeOccurrence?.onPath ? undefined : activeOccurrence?.shapeId
  const wordStart = activeOccurrence?.start
  const wordEnd = activeOccurrence?.end
  const isWordHidden = activeOccurrence?.overflow === true
  const hasWord = wordShapeId !== undefined && wordStart !== undefined && wordEnd !== undefined

  // Telling the canvas is syncing with something outside React, so it is an effect.
  useEffect(() => {
    onFocusWord(
      wordShapeId !== undefined && wordStart !== undefined && wordEnd !== undefined
        ? { shapeId: wordShapeId, start: wordStart, end: wordEnd, overflow: isWordHidden }
        : null
    )
  }, [wordShapeId, wordStart, wordEnd, isWordHidden, onFocusWord])

  // Leaving the panel (another tool, or the panel collapsing) ends the peek and clears the word.
  useEffect(() => () => onFocusWord(null), [onFocusWord])

  // The popover opens under the word on the canvas, as in the design demo, with Show underlines on
  // or off. It stays beside its row only when the word is not on the canvas to open under.
  const {
    anchor: wordAnchor,
    isSearching: isFindingWord,
    boundary: canvasBoundary,
  } = useSpellWordAnchor(
    hasWord ? { shapeId: wordShapeId, start: wordStart, end: wordEnd } : null,
    // Stepping to the next instance of the same word keeps the popover where it is until it moves.
    activeIssue?.id ?? null
  )

  // Clicking a flagged word on the canvas opens its popover and so highlights its row. On Edit
  // Dictionary there is no popover to open, so the click goes to the canvas as usual.
  const openFromCanvas = useCallback(
    (id: string, occurrenceIndex: number) => setActiveIssue({ id, occurrenceIndex }),
    []
  )

  useSpellCanvasClick({
    enabled: tab === 'check' && status === 'ready' && state.showUnderlines,
    issues: state.issues,
    onOpen: openFromCanvas,
  })

  // Keeps the open word's row in view in a long list, so a word picked on the canvas is seen lit.
  const listRef = useRef<HTMLUListElement>(null)
  const activeIssueId = activeIssue?.id

  useEffect(() => {
    if (activeIssueId === undefined) return

    listRef.current
      ?.querySelector(`[data-issue-id="${CSS.escape(activeIssueId)}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIssueId])

  const handleCaseSensitiveChange = (caseSensitive: boolean) => {
    if (
      activeIssue &&
      activeOccurrence?.shapeId !== undefined &&
      activeOccurrence.start !== undefined
    ) {
      setActiveIssue({
        ...activeIssue,
        anchor: { shapeId: activeOccurrence.shapeId, start: activeOccurrence.start },
      })
    }

    onStateChange({ ...state, caseSensitive })
  }

  const { addToDictionary, changeAndAdd } = usePopoverDictionaryAdd({
    state,
    onStateChange,
    onAddWord,
  })

  // After resolving an instance, stay on the same word at its next open instance (wrapping to
  // the first), or close the popover once none are left — the row itself then leaves the list.
  // Only while the popover is still on that word: a save to the dictionary can finish after the
  // user has moved to another word, and must not pull them back.
  const showNextOpen = (issueId: string, next: SpellCheckState, fromIndex: number) => {
    const issue = next.issues.find((i) => i.id === issueId)

    setActiveIssue((current) => {
      if (current?.id !== issueId) return current

      if (!issue || openOccurrenceCount(issue) === 0) return null

      const nextOpen = issue.occurrences.findIndex((o, i) => i > fromIndex && o.status === 'open')

      return {
        id: issueId,
        occurrenceIndex: nextOpen === -1 ? firstOpenOccurrenceIndex(issue) : nextOpen,
      }
    })
  }

  const applyToIssue = (issueId: string, next: SpellCheckState) => {
    onStateChange(next)
    showNextOpen(issueId, next, activeIssue?.occurrenceIndex ?? 0)
  }

  const renderIssueRow = (issue: SpellIssue) => {
    const isOpen = activeIssue?.id === issue.id
    const occurrenceIndex = isOpen ? activeIssue.occurrenceIndex : firstOpenOccurrenceIndex(issue)

    return (
      <li key={issue.id} data-issue-id={issue.id}>
        <Popover
          open={isOpen}
          onOpenChange={(open) =>
            setActiveIssue(
              open ? { id: issue.id, occurrenceIndex: firstOpenOccurrenceIndex(issue) } : null
            )
          }
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-10 w-full items-center justify-between rounded-md p-1 text-left outline-none hover:bg-accent focus-visible:bg-accent data-[state=open]:bg-accent"
            >
              <span className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5">
                <Badge variant="outline" size="sm" className={ROW_BADGE_CLASS}>
                  {openOccurrenceCount(issue)}
                </Badge>
                <span className="truncate text-sm font-medium text-foreground">{issue.word}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5 pr-2">
                {issue.onPath && (
                  <Badge variant="outline" size="sm" className={ROW_BADGE_CLASS}>
                    On path
                  </Badge>
                )}
                {issue.overflow && (
                  <Badge variant="outline" size="sm" className={ROW_BADGE_CLASS}>
                    Overflow
                  </Badge>
                )}
                <ChevronRight className="size-4 text-foreground" />
              </span>
            </button>
          </PopoverTrigger>
          {isOpen && wordAnchor && <PopoverAnchor virtualRef={wordAnchor} />}
          <PopoverContent
            side={isOpen && wordAnchor ? 'bottom' : 'right'}
            align="start"
            sideOffset={isOpen && wordAnchor ? 8 : 16}
            // Kept inside the canvas the word is on, so it never covers the panels beside it.
            collisionBoundary={isOpen && canvasBoundary ? canvasBoundary : undefined}
            collisionPadding={12}
            // Held back, out of sight, until the word is found and the canvas has moved to it, so it
            // shows directly in its place, with nothing sliding or jumping on the way there.
            className={cn('w-[352px]', isOpen && isFindingWord && 'invisible')}
            // Case Sensitive can be flipped with the popover open; the word is then checked again.
            // Switching tabs keeps the word open for the way back to Check Spelling. Another row
            // is not an outside click: it opens its own popover, which replaces this one in the
            // same click (SC-11), instead of this click only closing it.
            onInteractOutside={(event) => {
              const target = event.target as Node

              if (
                caseSensitiveRowRef.current?.contains(target) ||
                tabsListRef.current?.contains(target) ||
                (target instanceof Element && listRef.current?.contains(target))
              ) {
                event.preventDefault()
              }
            }}
          >
            <SpellFixPopoverContent
              key={issue.id}
              issue={issue}
              getSuggestions={getSuggestions}
              occurrenceIndex={occurrenceIndex}
              onOccurrenceIndexChange={(index) =>
                setActiveIssue({ id: issue.id, occurrenceIndex: index })
              }
              onChange={(replacement) =>
                applyToIssue(
                  issue.id,
                  changeOccurrence(state, issue.id, occurrenceIndex, replacement)
                )
              }
              onChangeAll={(replacement) =>
                applyToIssue(issue.id, changeAllOccurrences(state, issue.id, replacement))
              }
              onIgnore={() =>
                applyToIssue(issue.id, ignoreOccurrence(state, issue.id, occurrenceIndex))
              }
              onIgnoreAll={() => applyToIssue(issue.id, ignoreAllOccurrences(state, issue.id))}
              onAddToDictionary={async () => {
                // Adding always resolves this word, even when the custom dictionary toggle is off,
                // and only while it stays in the dictionary. A failed save leaves it flagged.
                if (await addToDictionary(issue)) {
                  setActiveIssue((current) => (current?.id === issue.id ? null : current))
                }
              }}
              onChangeAndAdd={async (replacement) => {
                const changed = await changeAndAdd(issue, occurrenceIndex, replacement)

                if (!changed) return false

                showNextOpen(changed.issueId, changed.next, occurrenceIndex)

                return true
              }}
            />
          </PopoverContent>
        </Popover>
      </li>
    )
  }

  return (
    // Full height, so Edit Dictionary's word list can fill what the panel has left.
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value === 'dictionary' ? 'dictionary' : 'check')}
      className="h-full gap-4"
    >
      <TabsList ref={tabsListRef} className="w-full">
        <TabsTrigger value="check">Check Spelling</TabsTrigger>
        <TabsTrigger value="dictionary">Edit Dictionary</TabsTrigger>
      </TabsList>

      <TabsContent value="check" className="flex flex-col gap-4">
        <SwitchRow
          label="Show underlines"
          checked={state.showUnderlines}
          onCheckedChange={(showUnderlines) => onStateChange({ ...state, showUnderlines })}
        />

        <Separator />

        <div className="flex flex-col gap-2">
          <p className="text-xs leading-none font-medium text-foreground">Applied Dictionaries</p>
          <div className="flex items-center gap-2">
            <Toggle
              pressed={state.dictionaries.custom}
              onPressedChange={(custom) =>
                onStateChange({ ...state, dictionaries: { ...state.dictionaries, custom } })
              }
            >
              Custom Dictionary
            </Toggle>
            <Toggle
              pressed={state.dictionaries.studentList}
              onPressedChange={(studentList) =>
                onStateChange({ ...state, dictionaries: { ...state.dictionaries, studentList } })
              }
            >
              Student List
            </Toggle>
          </div>
          <div ref={caseSensitiveRowRef}>
            <SwitchRow
              label="Case sensitive"
              checked={state.caseSensitive}
              onCheckedChange={handleCaseSensitiveChange}
            />
          </div>
        </div>

        <Separator />

        {status !== 'ready' ? (
          <SpellCheckStatusLine status={status} onRetry={onRetry} />
        ) : issues.length === 0 ? (
          <Alert className="border-success/60 bg-success/5 text-success [&>svg]:text-success">
            <CircleCheck className="size-4" />
            <AlertDescription>No spelling errors found on this spread.</AlertDescription>
          </Alert>
        ) : (
          <>
            <div>
              <Badge
                size="sm"
                className="border-transparent bg-warning text-warning-foreground shadow-none hover:bg-warning"
              >
                {misspellingCount} {misspellingCount === 1 ? 'Misspelling' : 'Misspellings'}
              </Badge>
            </div>
            <ul ref={listRef} className="flex flex-col gap-1">
              {issues.map(renderIssueRow)}
            </ul>
          </>
        )}
      </TabsContent>

      <TabsContent value="dictionary" className="flex min-h-0 flex-col">
        <EditDictionaryTab
          status={dictionaryStatus}
          customWords={state.customWords}
          caseSensitive={state.caseSensitive}
          onAddWord={onAddWord}
          onRemoveWord={onRemoveWord}
          onClearAll={onClearAll}
        />
      </TabsContent>
    </Tabs>
  )
}
